<template>
  <div id="app">
    <main v-if="estaLogueado && !esPantallaLogin" id="main" class="main">
      <SideBar/>
      <section class="wrapper-container">
        <NavBar :title="tituloModulo"/>
        <router-view class="wrapper-container__medio" />
        <FooterSection/>
      </section>
    </main>
    <main v-else class="main-login">
      <router-view/>
    </main>
  </div>
</template>

<script>
  //src/App.vue
  import "regenerator-runtime";
  import { mapActions, mapState } from "vuex";
  import "@splidejs/splide/dist/css/themes/splide-sea-green.min.css";
  import { EventBus } from '@/eventBus';
  import { AVISO_ANTES_MS, motivoCierreSesion, vencimientoToken } from '@/utils/sesion';
  const SideBar = () => import("@/components/SideBar.vue")
  const NavBar = () => import("@/components/NavBar.vue")
  const FooterSection = () => import("@/components/Section/SectionFooter.vue");


  export default {
    components: {
      SideBar,
      NavBar,
      FooterSection
    },
    data() {
      return {
        data: "",
        // idEmpresa / idPrivilegio / idGrupo / colorPrimario vienen del mixin
        // global, que los saca de loginModule. Ver src/main.js.

        //FF
        currentRoute: "",
        previousRoute: "",
        moduleRoute: window.location.pathname,
        tituloModulo: "",
      };
    },
    watch: {
      moduleRoute() {
        this.getNombreModulo();
      },
      // Cada vez que se inicia o se cierra sesión (App se crea una sola vez)
      estaLogueado: {
        immediate: true,
        handler() {
          this.revisarSesion();
          // Términos de la empresa (store "terminos"): se piden al entrar y se
          // limpian al salir. Se mira la sesión de nuevo, y no el valor que
          // trajo el watcher, porque revisarSesion pudo cerrarla recién (token
          // vencido) y la query saldría sin token.
          if (this.estaLogueado && localStorage.getItem("access-token")) {
            this.$store.dispatch("terminos/cargar");
          } else {
            this.$store.commit("terminos/RESET");
          }
        },
      },
    },
    
    computed: {
      ...mapState("sidebar", ["modules"]),

      // Login.vue guarda el usuario (y con eso estaLogueado pasa a true) antes
      // de terminar de cargar los módulos y navegar. Si el layout dependiera
      // solo de la sesión, en ese intervalo el Login se volvía a montar dentro
      // del router-view del administrador, con sidebar y navbar alrededor.
      esPantallaLogin() {
        return ["Login", "Login-token"].includes(this.$route.name);
      },
    },

    async created() {
      const data = {
        id_privilegio: this.idPrivilegio,
        id_grupo: this.idGrupo
      };
      if(data.id_privilegio!=undefined){
        await this.getModules(data);
        this.getNombreModulo();
      }

      EventBus.$on("logout", () => { this.logOut() });
      EventBus.$on("failedServerResponse", () => { this.toastServerError() });
    },

    beforeDestroy() {
      EventBus.$off("logout", () => { this.logOut() });
      EventBus.$on("failedServerResponse");
      document.removeEventListener("visibilitychange", this.alVolverALaPestana);
      this.cancelarTimersSesion();
    },

    updated() {
      this.moduleRoute = window.location.pathname;
    },

    mounted() {
      this.getNombreModulo();
      document.addEventListener("visibilitychange", this.alVolverALaPestana);
    },

    methods: {
      ...mapActions("sidebar", ["getModules"]),

      // Vencimiento de la sesión con la app abierta (al cargarla lo revisa
      // checkSessionExpiration en main.js). Ver utils/sesion.js.
      revisarSesion() {
        this.cancelarTimersSesion();
        if (!localStorage.getItem("access-token")) return;

        const motivo = motivoCierreSesion(this.$ls.get("remember_session"));
        if (motivo) {
          this.cerrarSesionVencida(motivo);
          return;
        }

        const vence = vencimientoToken();
        if (!vence) return;
        const restante = vence - Date.now();
        if (restante > AVISO_ANTES_MS) {
          this.timerAvisoSesion = setTimeout(this.avisarVencimiento, restante - AVISO_ANTES_MS);
        }
        // Al vencer se vuelve a revisar, y ahí se cierra
        this.timerCierreSesion = setTimeout(this.revisarSesion, restante + 1000);
      },

      cancelarTimersSesion() {
        clearTimeout(this.timerAvisoSesion);
        clearTimeout(this.timerCierreSesion);
      },

      // Con la pestaña en segundo plano o el equipo suspendido los timers se
      // atrasan: al volver se revisa de nuevo
      alVolverALaPestana() {
        if (document.visibilityState === "visible") this.revisarSesion();
      },

      avisarVencimiento() {
        this.$toast.open({
          message:
            "Tu sesión se cerrará en 5 minutos. Guarda lo que estés haciendo; después tendrás que volver a iniciar sesión.",
          type: "warning",
          duration: AVISO_ANTES_MS,
          position: "top-right",
        });
      },

      // "navegador" no se avisa: es lo que se pidió al no marcar "Recordar sesión"
      cerrarSesionVencida(motivo) {
        if (motivo === "token") {
          this.$toast.open({
            message: "Tu sesión expiró. Vuelve a iniciar sesión para continuar.",
            type: "info",
            duration: 8000,
            position: "top-right",
          });
        }
        this.logOut();
      },

      //FF
      getNombreModulo() {
        let modulo = "";
        let pathModulo = ""; 
        if(this.moduleRoute.includes('administrador')){
          modulo = this.moduleRoute.split('/', 4);
          pathModulo = "/" + modulo[2];
        } else {
          modulo = this.moduleRoute.split('/', 3);
          pathModulo = "/" + modulo[1];
        }
        
        // Módulos que no vienen de ws y que no se muestran en sidebar:
        // Mi Perfil y Personalización, dado que son transversales
        if (pathModulo === "/perfil") {
          this.tituloModulo = "Mi Perfil";
        } else if (pathModulo === "/customization") {
          this.tituloModulo = "Personalización";
        } else {   
          try {
            const found = this.modules?.find(element => element.moduloGeneral.ruta_admin === pathModulo);
            if (found) {
              this.tituloModulo = found.nombre_modulo
            }
          } catch(error) {
            console.warn("Error getNombreModulo - modules not loaded yet:", error);
          }
        }
      }
    },
  };

</script>

<style lang="scss">
html,
body {
  width: 100%;
  height: 100%;
  margin: 0px;

  input:focus,
  select:focus,
  textarea:focus,
  button:focus {
    outline: none;
  }
  input:focus-visible {
    outline: none;
  }
  input[type="text"] {
    font-size: 15px;
  }
  input[type="number"] {
    color: #6B6E7F;
  }
  ul {
    padding: 0;
    list-style: none;
    margin: 0;
  }

}
</style>
